import { useCallback, useEffect, useRef, useState } from 'react';
import { api, describeError } from '../api.ts';
import { announceChange } from '../ui/changes.ts';
import { companionApi } from './api.ts';
import type { CompanionStatus, Proposal, Values } from './types.ts';
import { useWandStatus } from './WandUi.tsx';
import {
  WAND_OPERATION,
  addEntries,
  entriesFor,
  entriesFrom,
  orphanedRefs,
  refInUse,
  removeEntry,
  savePlan,
  setAccepted,
  wandKey,
  type WandEntries,
  type WandEntry,
  type WandField,
  type WandInput,
  type WandOutput,
} from './wand.ts';

type Toast = (kind: 'success' | 'error' | 'info', message: string) => void;

export interface LastSave {
  ref: string;
  label: string;
  undoUntil: string;
}

export interface WandController {
  status: CompanionStatus | null;
  entries: WandEntries;
  busy: (key: string) => boolean;
  /** Ask for proposals; `busyKey` is the field key (or "all") whose icon shows that it is thinking. */
  ask: (busyKey: string, input: Omit<WandInput, 'couponRef'>) => Promise<void>;
  accept: (key: string) => void;
  reject: (key: string) => void;
  retry: (entry: WandEntry, note: string) => Promise<void>;
  /** The fields one site's person typed themselves; the wand leaves them alone. */
  reportTyped: (siteRef: string | null, fields: WandField[]) => void;
  /**
   * Saves one site (or the facts, siteRef null) through its wand changeset when a proposal was accepted: the draft's
   * values are confirmed (ProposalService → the coupon operations, audited, undoable). Returns false when there was
   * nothing to confirm (the caller saves normally); every other open changeset of the site is discarded either way.
   */
  saveThroughWand: (siteRef: string | null, label: string, values: Values) => Promise<'saved' | 'failed' | 'none'>;
  lastSave: LastSave | null;
  undo: () => Promise<void>;
}

export function useWand(couponRef: string, toast: Toast): WandController {
  const status = useWandStatus();
  const [entries, setEntries] = useState<WandEntries>({});
  const current = useRef<WandEntries>({});
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(new Set());
  const [lastSave, setLastSave] = useState<LastSave | null>(null);
  const typed = useRef(new Map<string, ReadonlySet<WandField>>());

  /** Every change goes through here so changesets nobody refers to any more are discarded. */
  const update = useCallback((change: (entries: WandEntries) => WandEntries, keep: readonly string[] = []) => {
    const previous = current.current;
    const next = change(previous);
    current.current = next;
    setEntries(next);
    for (const ref of orphanedRefs(previous, next)) if (!keep.includes(ref)) void companionApi.discard(ref).catch(() => undefined);
  }, []);

  // Leaving the coupon discards its open proposals (they were never saved).
  useEffect(
    () => () => {
      for (const ref of new Set(Object.values(current.current).map((entry) => entry.changesetRef))) if (ref) void companionApi.discard(ref).catch(() => undefined);
    },
    [],
  );

  const setBusy = (key: string, on: boolean) =>
    setBusyKeys((keys) => {
      const next = new Set(keys);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const run = async (busyKey: string, input: WandInput, replaced: readonly string[] = []) => {
    setBusy(busyKey, true);
    try {
      const output = await api.op<WandOutput>(WAND_OPERATION, input);
      if (!output.configured) {
        toast('error', output.message ?? 'The magic wand is not configured.');
        return;
      }
      if (output.message) toast('error', output.message);
      const added = entriesFrom(output, (siteRef, field) => typed.current.get(siteRef ?? 'facts')?.has(field) ?? false);
      update((entries) => addEntries(entries, added), replaced);
      // Proposals for fields the person typed in were skipped; their changesets are of no use.
      const used = new Set(added.map((entry) => entry.changesetRef));
      for (const proposal of output.proposals) if (proposal.changesetRef && !used.has(proposal.changesetRef)) void companionApi.discard(proposal.changesetRef).catch(() => undefined);
      if (added.length === 0) toast('info', 'Nothing to propose: you already typed in these fields.');
    } catch (error) {
      toast('error', describeError(error));
    } finally {
      setBusy(busyKey, false);
    }
  };

  return {
    status,
    entries,
    busy: (key) => busyKeys.has(key) || busyKeys.has('all'),
    ask: (busyKey, input) => run(busyKey, { couponRef, ...input }),
    accept: (key) => update((entries) => setAccepted(entries, key)),
    reject: (key) => update((entries) => removeEntry(entries, key)),
    retry: async (entry, note) => {
      // "Try again" replaces the changeset unless another field still waits on it.
      const replaces = entry.changesetRef && !refInUse(current.current, entry.changesetRef, entry.key) ? [entry.changesetRef] : [];
      await run(
        entry.key,
        { couponRef, ...(entry.siteRef ? { siteRefs: [entry.siteRef] } : {}), fields: [entry.field], ...(note ? { note } : {}), replaces },
        replaces,
      );
    },
    reportTyped: (siteRef, fields) => {
      typed.current.set(siteRef ?? 'facts', new Set(fields));
    },
    saveThroughWand: async (siteRef, label, values) => {
      const plan = savePlan(current.current, siteRef);
      const keys = entriesFor(current.current, siteRef).map((entry) => entry.key);
      const clear = () => update((entries) => keys.reduce((next, key) => removeEntry(next, key), entries), plan.confirm ? [plan.confirm.ref] : []);
      if (!plan.confirm || Object.keys(values).length === 0) {
        // Nothing accepted (or the text is back to what the site had): every open changeset of the site goes.
        if (plan.confirm) void companionApi.discard(plan.confirm.ref).catch(() => undefined);
        clear();
        return 'none';
      }
      for (const ref of plan.discard) void companionApi.discard(ref).catch(() => undefined);
      let proposal: Proposal;
      try {
        proposal = await companionApi.confirm(plan.confirm.ref, { rows: [{ id: plan.confirm.rowId, included: true, proposed: values }] });
      } catch (error) {
        toast('error', describeError(error));
        clear();
        void companionApi.discard(plan.confirm.ref).catch(() => undefined);
        return 'failed';
      }
      clear();
      if (proposal.status !== 'applied') {
        toast('error', proposal.error ?? `${label}: not saved`);
        announceChange();
        return 'failed';
      }
      if (proposal.undoUntil) setLastSave({ ref: proposal.ref, label, undoUntil: proposal.undoUntil });
      return 'saved';
    },
    lastSave,
    undo: async () => {
      if (!lastSave) return;
      try {
        await companionApi.undo(lastSave.ref);
        toast('success', `${lastSave.label}: change undone`);
        setLastSave(null);
        announceChange();
      } catch (error) {
        toast('error', describeError(error));
      }
    },
  };
}

export { wandKey };
